/**
 * Encounter and dialogue.
 *
 * Blocked choices are shown greyed rather than hidden: knowing you could have
 * opened that door with the right item is what makes someone play again
 * (encounter.ts, GDD §15 Choice Replay Rate). Choices hidden behind waiting are
 * the exception — revealing them would spoil the mystery.
 */

import { bandOf, relationshipStage, type DiceFace, type GameSession } from '@lastroll/engine';
import { expressionFor, portraitUri } from './art.js';
import { npcPortraitArt } from './producedArt.js';

const STAGE_LABEL: Record<string, string> = {
  stranger: '낯선 사람',
  acquaintance: '아는 사이',
  companion: '동행',
  friend: '친구',
  trusted: '신뢰',
  confidant: '비밀을 나눔',
  family: '가족 같은',
};

export interface EncounterPanelProps {
  session: GameSession;
  onChoose: (choiceId: string, face?: DiceFace | null) => void;
  onWait: () => void;
  onRollForChoice: () => void;
}

export function EncounterPanel({
  session,
  onChoose,
  onWait,
  onRollForChoice,
}: EncounterPanelProps): JSX.Element | null {
  const runtime = session.encounter;
  if (!runtime) return null;

  const def = runtime.def;
  const choices = session.choices();
  const pending = session.pendingRoll;
  const npcId = def.npcId;
  const npc = npcId ? session.state.npcs[npcId] : undefined;
  const lines = npcId ? session.dialogueFor(npcId) : [];

  return (
    <div className="panel">
      {npcId && npc && (
        <div className="speaker">
          {/*
            그린 초상이 있으면 그것을 — 표정은 한 장(중립)뿐이라, NPC 의 기억(신뢰·원한)은
            테두리 색과 필터로 보인다(Style Bible §5 「기억이 한눈에」). 없으면 절차적 초상 + 표정 오버레이.
          */}
          <img
            className={`speaker__portrait${npcPortraitArt(npcId) ? ` speaker__portrait--art mood--${expressionFor(npc)}` : ''}`}
            src={npcPortraitArt(npcId) ?? portraitUri(session.npcName(npcId), 'npc', expressionFor(npc), npcId, false)}
            alt=""
          />
          <div>
            <div className="speaker__name">{session.npcName(npcId)}</div>
            <div className="speaker__stage">
              {STAGE_LABEL[relationshipStage(npc)] ?? relationshipStage(npc)}
              {npc.metCount > 1 && ` · ${npc.metCount}번째 만남`}
            </div>
            {(npc.trust !== 0 || npc.resentment !== 0) && (
              <div className="speaker__rel">
                {npc.trust !== 0 && <span className="rel__trust">신뢰 {npc.trust}</span>}
                {npc.resentment > 0 && <span className="rel__resent">원한 {npc.resentment}</span>}
              </div>
            )}
          </div>
        </div>
      )}

      <h2 className="panel__title">{def.title}</h2>
      <p className="panel__body">{def.body}</p>

      {lines.length > 0 && (
        <div className="lines">
          {lines.map((l) => (
            <p key={`${l.slot}-${l.index}`} className="line">
              {l.text}
            </p>
          ))}
        </div>
      )}

      {pending && (
        <p className="muted">
          굴린 눈 <strong style={{ color: 'var(--candle)' }}>{pending.finalFace}</strong> — 이
          눈으로 결과가 갈린다.
        </p>
      )}

      <div className="choices">
        {choices.map(({ choice, available, blockedBy }) => {
          const needsRoll = Boolean(choice.roll) && !pending;
          return (
            <button
              key={choice.id}
              type="button"
              className={`choice${available ? '' : ' is-blocked'}`}
              disabled={!available}
              onClick={() =>
                needsRoll ? onRollForChoice() : onChoose(choice.id, pending?.finalFace ?? null)
              }
            >
              <span>{choice.label}</span>
              {choice.roll && (
                <span className="choice__tag tag--roll">
                  {pending ? `${bandOf(pending.finalFace as DiceFace)} 판정` : '주사위 필요'}
                </span>
              )}
              {!available && blockedBy === 'item' && (
                <span className="choice__tag tag--item">아이템 필요</span>
              )}
              {!available && blockedBy === 'fact' && (
                <span className="choice__tag tag--fact">단서 필요</span>
              )}
              {!available && blockedBy === 'condition' && (
                <span className="choice__tag tag--item">조건 불충분</span>
              )}
            </button>
          );
        })}
      </div>

      <button type="button" className="wait-btn" onClick={onWait}>
        …아무것도 하지 않고 기다린다 ({runtime.waits})
      </button>
    </div>
  );
}
